import type { MarketGroupsApi } from "@/v2/api/metadata/market-groups";
import type { MarketsApi } from "@/v2/api/metadata/markets";
import type { ContentTab, DialogTabSaveContext } from "@/v2/framework/dialog/types";
import { mergeEntityPatches } from "@/v2/framework/utils/mergeEntityPatches";
import {
  buildSupplementalNamesPatch,
  syncSupplementalNamesBaseline,
} from "@/v2/kits/supplemental-names";
import { createInheritableTabSave } from "@/v2/kits/tenant-inheritance";
import { marketGroupPositionValidator, marketGroupStatusOptions } from "../../../../shared";
import {
  buildMarketGroupCollectionsPatch,
  syncMarketGroupCollectionsBaseline,
} from "../../collections";
import { marketGroupId, notifySuccess } from "../shared";

const mainTabChildren: ContentTab["children"] = [
  {
    type: "select-field",
    name: "status",
    label: "Status",
    required: true,
    placeholder: "Select status",
    options: marketGroupStatusOptions,
    inheritance: { computedKey: "computedStatus", parentKey: "parentStatus" },
  },
  {
    type: "text-field",
    name: "name",
    label: "Name",
    placeholder: "Enter name",
    inheritance: { computedKey: "computedName", parentKey: "parentName" },
    validate: (value, values) => {
      if (values.isTenantSourceOfEntity && !String(value ?? "").trim()) return "Required";
      return undefined;
    },
  },
  {
    type: "text-field",
    name: "info",
    label: "Info",
    placeholder: "Enter info",
    inheritance: { computedKey: "computedInfo", parentKey: "parentInfo" },
  },
  {
    type: "text-field",
    name: "position",
    label: "Position",
    placeholder: "Enter position",
    inputType: "number",
    validate: marketGroupPositionValidator,
    inheritance: { computedKey: "computedPosition", parentKey: "parentPosition" },
  },
];

export function createMainTab(options: {
  marketGroupsApi: MarketGroupsApi;
  marketsApi: MarketsApi;
}): ContentTab {
  const { marketGroupsApi, marketsApi } = options;

  const save = createInheritableTabSave({
    children: mainTabChildren,
    buildExtraPatch: (ctx) => {
      const supplemental = buildSupplementalNamesPatch(ctx.values, ctx.baseline);
      const collections = buildMarketGroupCollectionsPatch(ctx.values, ctx.baseline);
      const merged = supplemental ? mergeEntityPatches(supplemental, collections ?? {}) : collections;
      return merged && Object.keys(merged).length ? merged : null;
    },
    afterPersist: (ctx) => {
      syncSupplementalNamesBaseline(ctx);
      syncMarketGroupCollectionsBaseline(ctx.values, ctx.baseline);
    },
    persist: async (patch, ctx: DialogTabSaveContext) => {
      await marketGroupsApi.update(marketGroupId(ctx.values), patch);
      notifySuccess();
    },
  });

  return {
    id: "main",
    label: "Main",
    save,
    children: [
      ...mainTabChildren,
      {
        type: "component",
        component: () => import("../../MarketGroupCollectionsFields.vue"),
        props: { mode: "edit", marketsApi },
      },
      {
        type: "component",
        component: () =>
          import("@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue"),
        props: { mode: "edit", entity: "MARKET_GROUP" },
      },
    ],
  };
}
